<template>
  <section class="stack-card">
    <h4 class="stack-card__title">Stack</h4>
    <div
      class="stack-card__carousel"
      ref="stackCarousel"
      @mousedown="startStackDrag"
      @touchstart="startStackDrag"
      :class="{ 'is-dragging': isStackDragging }"
    >
      <div class="stack-card__track" ref="stackTrack" :style="stackTrackStyle">
        <!-- First set of icons -->
        <div
          v-for="(item, index) in stackIcons"
          :key="'first-' + index"
          class="stack-icon"
          :data-name="item.name"
        >
          <component :is="item.icon" />
          <span class="stack-icon__name">{{ item.name }}</span>
        </div>
        <!-- Duplicate set for infinite scroll -->
        <div
          v-for="(item, index) in stackIcons"
          :key="'second-' + index"
          class="stack-icon"
          :data-name="item.name"
        >
          <component :is="item.icon" />
          <span class="stack-icon__name">{{ item.name }}</span>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, markRaw } from 'vue'
import { JavaScriptIcon, FigmaIcon, AdobeXdIcon } from 'hugeicons-vue'
import VueIcon from '@/components/icons/VueIcon.vue'
import GSAPIcon from '@/components/icons/GSAPIcon.vue'
import DockerIcon from '@/components/icons/DockerIcon.vue'
import TailwindCssIcon from '@/components/icons/TailwindCssIcon.vue'
import GitIcon from '@/components/icons/GitIcon.vue'
import Css3Icon from '@/components/icons/Css3Icon.vue'
import Html5Icon from '@/components/icons/Html5.vue'
// Stack icons configuration — aligned with Product + UX + Frontend
const stackIcons = [
  // Product & UX
  { name: 'Product Design' },
  { name: 'UX Strategy' },
  { name: 'Design Systems' },
  { name: 'UX Research' },
  { name: 'Information Architecture' },

  // Design tools
  { name: 'Figma', icon: markRaw(FigmaIcon) },
  { name: 'Adobe XD', icon: markRaw(AdobeXdIcon) },

  // Frontend
  { name: 'HTML5', icon: markRaw(Html5Icon) },
  { name: 'CSS', icon: markRaw(Css3Icon) },
  { name: 'JavaScript', icon: markRaw(JavaScriptIcon) },
  { name: 'Vue Js', icon: markRaw(VueIcon), isCustom: true },
  { name: 'Tailwind CSS', icon: markRaw(TailwindCssIcon) },
  { name: 'GSAP', icon: markRaw(GSAPIcon) },

  // Engineering & quality
  { name: 'Component Architecture' },
  { name: 'Performance Optimization' },
  { name: 'Accessibility (a11y)' },

  // Tooling
  { name: 'Git', icon: markRaw(GitIcon) },
  { name: 'Docker', icon: markRaw(DockerIcon) },
]

// Stack carousel drag with inertia
const stackCarousel = ref(null)
const stackTrack = ref(null)
const isStackDragging = ref(false)
const stackPosition = ref(0)
const autoScrollSpeed = 0.5 // pixels per frame for auto-scroll

let stackDragStartX = 0
let stackDragStartPosition = 0
let stackVelocity = 0
let stackLastX = 0
let stackLastTime = 0
let stackInertiaFrame = null
let stackAutoScrollFrame = null
let trackWidth = 0
let halfTrackWidth = 0

// Computed style for the track
const stackTrackStyle = computed(() => ({
  transform: `translateX(${stackPosition.value}px)`,
}))

// Start dragging
const startStackDrag = (e) => {
  e.preventDefault()
  isStackDragging.value = true

  // Cancel any ongoing animations
  if (stackInertiaFrame) cancelAnimationFrame(stackInertiaFrame)
  if (stackAutoScrollFrame) cancelAnimationFrame(stackAutoScrollFrame)

  const clientX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX
  stackDragStartX = clientX
  stackDragStartPosition = stackPosition.value
  stackLastX = clientX
  stackLastTime = performance.now()
  stackVelocity = 0

  // Calculate track width for looping
  if (stackTrack.value) {
    trackWidth = stackTrack.value.scrollWidth
    halfTrackWidth = trackWidth / 2
  }

  document.addEventListener('mousemove', onStackDrag)
  document.addEventListener('mouseup', stopStackDrag)
  document.addEventListener('touchmove', onStackDrag, { passive: false })
  document.addEventListener('touchend', stopStackDrag)
}

// During drag
const onStackDrag = (e) => {
  if (!isStackDragging.value) return
  e.preventDefault()

  const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX
  const currentTime = performance.now()
  const deltaTime = currentTime - stackLastTime

  // Calculate velocity for inertia
  if (deltaTime > 0) {
    stackVelocity = ((clientX - stackLastX) / deltaTime) * 16 // normalize to ~60fps
  }

  stackLastX = clientX
  stackLastTime = currentTime

  const deltaX = clientX - stackDragStartX
  let newPosition = stackDragStartPosition + deltaX

  // Infinite loop: wrap position
  newPosition = wrapPosition(newPosition)
  stackPosition.value = newPosition
}

// Wrap position for infinite scrolling
const wrapPosition = (pos) => {
  if (halfTrackWidth === 0) return pos

  // Keep position within bounds for seamless looping
  while (pos > 0) {
    pos -= halfTrackWidth
  }
  while (pos < -halfTrackWidth) {
    pos += halfTrackWidth
  }
  return pos
}

// Stop dragging and apply inertia
const stopStackDrag = () => {
  if (!isStackDragging.value) return
  isStackDragging.value = false

  document.removeEventListener('mousemove', onStackDrag)
  document.removeEventListener('mouseup', stopStackDrag)
  document.removeEventListener('touchmove', onStackDrag)
  document.removeEventListener('touchend', stopStackDrag)

  // Apply inertia if there's velocity
  if (Math.abs(stackVelocity) > 0.5) {
    applyInertia()
  } else {
    // Resume auto-scroll
    startAutoScroll()
  }
}

// Inertia animation
const applyInertia = () => {
  const friction = 0.95 // Higher = more slide
  const minVelocity = 0.1

  const animate = () => {
    stackVelocity *= friction

    if (Math.abs(stackVelocity) < minVelocity) {
      stackVelocity = 0
      // Resume auto-scroll when inertia ends
      startAutoScroll()
      return
    }

    let newPosition = stackPosition.value + stackVelocity
    newPosition = wrapPosition(newPosition)
    stackPosition.value = newPosition

    stackInertiaFrame = requestAnimationFrame(animate)
  }

  stackInertiaFrame = requestAnimationFrame(animate)
}

// Auto-scroll animation (continuous movement)
const startAutoScroll = () => {
  if (stackAutoScrollFrame) cancelAnimationFrame(stackAutoScrollFrame)

  const autoScroll = () => {
    if (isStackDragging.value) return

    let newPosition = stackPosition.value - autoScrollSpeed
    newPosition = wrapPosition(newPosition)
    stackPosition.value = newPosition

    stackAutoScrollFrame = requestAnimationFrame(autoScroll)
  }

  stackAutoScrollFrame = requestAnimationFrame(autoScroll)
}

// Initialize track width and start auto-scroll
const initStackCarousel = () => {
  if (stackTrack.value) {
    trackWidth = stackTrack.value.scrollWidth
    halfTrackWidth = trackWidth / 2
  }
  startAutoScroll()
}

onMounted(() => {
  initStackCarousel()
})

onUnmounted(() => {
  // Clean up animations
  if (stackInertiaFrame) cancelAnimationFrame(stackInertiaFrame)
  if (stackAutoScrollFrame) cancelAnimationFrame(stackAutoScrollFrame)
  document.removeEventListener('mousemove', onStackDrag)
  document.removeEventListener('mouseup', stopStackDrag)
  document.removeEventListener('touchmove', onStackDrag)
  document.removeEventListener('touchend', stopStackDrag)
})
</script>

<style scoped>
.stack-card {
  @apply py-6 h-full relative w-full;
}

.stack-card__title {
  @apply text-xs uppercase tracking-wider mb-4 pl-6;
  color: var(--color-text-muted);
}

.stack-card__carousel {
  @apply relative overflow-hidden h-[56px] flex items-center w-full touch-none;
  mask-image: linear-gradient(to right, transparent 0%, black 10%, black 70%, transparent 100%);
  -webkit-mask-image: linear-gradient(
    to right,
    transparent 0%,
    black 10%,
    black 90%,
    transparent 100%
  );
  cursor: grab;
  user-select: none;
  -webkit-user-select: none;
}

.stack-card__carousel.is-dragging {
  cursor: grabbing;
}

.stack-card__track {
  @apply flex gap-4;
  width: max-content;
  will-change: transform;
}

.stack-icon {
  @apply rounded-xl flex flex-row items-center justify-center transition-all duration-300 cursor-pointer flex-shrink-0 gap-2 px-3;
  height: 40px;
  background: var(--color-surface-elevated);
  pointer-events: none;
  /* Prevent icons from capturing drag events */
}

.stack-icon__name {
  @apply text-sm font-medium;
  color: var(--color-text-secondary);
  white-space: nowrap;
}

.stack-icon svg {
  @apply w-6 h-6;
  color: var(--color-text-secondary);
}

.stack-icon:hover {
  background: var(--color-electric-purple);
  transform: translateY(-4px) scale(1.1);
}

.stack-icon:hover svg {
  color: white;
}
</style>
